import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnChanges,
  inject,
  input,
  output,
  ViewChild,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormsModule, NgForm} from "@angular/forms";
import {formatDate} from "@angular/common";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatInputModule} from "@angular/material/input";
import {DateAdapter} from "@angular/material/core";
import {MyDateAdapter} from "src/app/helpers/MyDateAdapter";
import {CivilDatePipe} from "src/app/modules/shared/pipes/civil-date.pipe";
import type {HttpErrorResponse} from "@angular/common/http";
import {TranslateModule} from "@ngx-translate/core";
import {Subscription, finalize} from "rxjs";
import type {CompletionWorksModel} from "src/app/models/WorksModel";
import {isBook} from "src/app/utils/works-type-guards.util";
import type {
  WorkTimeHistory,
  WorkTimeSession,
  PersonalTimePayload,
} from "src/app/models/WorkTime";
import {WorkTimeService} from "src/app/modules/shared/services/work-time.service";
import {DurationPipe} from "src/app/modules/shared/pipes/duration.pipe";
import {GameSessionDurationComponent} from "../game-session-duration/game-session-duration.component";

@Component({
  selector: "app-work-time-history",
  templateUrl: "./work-time-history.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: DateAdapter, useClass: MyDateAdapter}],
  imports: [
    FormsModule,
    MatDatepickerModule,
    MatInputModule,
    CivilDatePipe,
    TranslateModule,
    DurationPipe,
    GameSessionDurationComponent,
  ],
})
export class WorkTimeHistoryComponent implements OnChanges {
  readonly item = input.required<CompletionWorksModel>();
  readonly userId = input.required<string>();
  readonly disabled = input(false);
  readonly saveRequested = output<void>();
  readonly edited = output<void>();
  @ViewChild(NgForm) sessionForm?: NgForm;
  private readonly service = inject(WorkTimeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private request?: Subscription;
  private mutation?: Subscription;
  private identity?: string;
  private requestedRevision?: number;
  private operation?: PersonalTimePayload;
  history?: WorkTimeHistory;
  editing?: WorkTimeSession;
  deleting = false;
  minutes = 0;
  pages = 0;
  sessionDate: Date | null = null;
  readonly maxDate = new Date();
  loading = false;
  pending = false;
  error = "";

  get book(): boolean {
    return isBook(this.item());
  }
  get editingPages(): boolean {
    return this.book && this.editing?.pageFrom !== undefined;
  }
  get pageAfter(): number {
    const previous =
      (this.editing?.pageTo ?? 0) - (this.editing?.pageFrom ?? 0);
    return (
      (this.history?.currentPage ?? 0) +
      (this.deleting ? 0 : this.pages) -
      previous
    );
  }
  get readingMinutes(): number {
    return (
      Math.round(this.pages * (this.history?.readingMinutesPerPage ?? 0) * 10) /
      10
    );
  }

  ngOnChanges(): void {
    const identity = `${this.userId()}:${this.item().type}:${this.item()._id}`;
    if (identity === this.identity) {
      const revision = this.item().timeSummary?.revision;
      if (
        !this.pending &&
        !this.editing &&
        revision !== undefined &&
        revision > (this.requestedRevision ?? -1)
      )
        this.load();
      return;
    }
    this.identity = identity;
    this.request?.unsubscribe();
    this.mutation?.unsubscribe();
    this.history = undefined;
    this.editing = undefined;
    this.operation = undefined;
    this.load();
  }

  load(more = false): void {
    this.request?.unsubscribe();
    this.requestedRevision = this.item().timeSummary?.revision;
    this.loading = true;
    this.error = "";
    this.request = this.service
      .sessions(
        this.userId(),
        this.item(),
        more ? this.history?.nextCursor : undefined
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (value) => {
          this.requestedRevision = Math.max(
            this.requestedRevision ?? -1,
            value.revision
          );
          const sessions = new Map(
            (more ? this.history?.sessions ?? [] : []).map((row) => [
              row.day,
              row,
            ])
          );
          for (const row of value.sessions) sessions.set(row.day, row);
          if (this.editing && !sessions.has(this.editing.day))
            sessions.set(this.editing.day, this.editing);
          this.history = {
            ...value,
            sessions: [...sessions.values()].sort((left, right) =>
              right.day.localeCompare(left.day)
            ),
          };
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = "TIME_TRACKING.LOAD_ERROR";
          this.cdr.markForCheck();
        },
      });
  }

  edit(session: WorkTimeSession): void {
    this.edited.emit();
    this.deleting = false;
    this.editing = session;
    this.sessionDate = new Date(`${session.day}T12:00:00`);
    this.minutes = session.minutes;
    this.pages = (session.pageTo ?? 0) - (session.pageFrom ?? 0);
    this.operation = undefined;
    this.error = "";
  }

  changed(): void {
    this.operation = undefined;
    this.error = "";
    this.edited.emit();
  }

  remove(session: WorkTimeSession): void {
    this.edit(session);
    this.deleting = true;
  }

  cancel(): void {
    this.editing = undefined;
    this.deleting = false;
    this.operation = undefined;
    this.error = "";
  }

  save(onSaved?: () => void): void {
    if (
      !this.editing ||
      !this.history ||
      this.pending ||
      this.disabled() ||
      this.loading ||
      this.history.source !== "manual"
    )
      return;
    if (!this.validate()) return;
    const newSessionDay = this.sessionDate
      ? formatDate(this.sessionDate, "yyyy-MM-dd", "en")
      : this.editing.day;
    this.operation ??= {
      operationId: crypto.randomUUID(),
      expectedRevision: this.history.revision,
      ...(this.editingPages
        ? {sessionPages: this.deleting ? 0 : this.pages}
        : {dailyMinutes: this.deleting ? 0 : this.minutes}),
      sessionDay: this.editing.day,
      ...(!this.deleting && newSessionDay !== this.editing.day
        ? {newSessionDay}
        : {}),
    };
    this.pending = true;
    this.error = "";
    this.cdr.markForCheck();
    this.mutation = this.service
      .save(this.userId(), this.item(), this.operation)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pending = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.editing = undefined;
          this.operation = undefined;
          if (onSaved) onSaved();
          else this.load();
        },
        error: (error: HttpErrorResponse) => {
          this.error =
            error.status === 422
              ? "TIME_TRACKING.INVALID_SESSION_EDIT"
              : "TIME_TRACKING.SAVE_ERROR";
          if (error.status === 409) {
            this.operation = undefined;
            this.load();
            this.error = "TIME_TRACKING.CONFLICT";
          }
          this.cdr.markForCheck();
        },
      });
  }

  private validate(): boolean {
    this.sessionForm?.form.markAllAsTouched();
    if (
      !this.deleting &&
      (!this.sessionDate ||
        !Number.isFinite(this.sessionDate.getTime()) ||
        this.sessionForm?.controls["sessionDate"]?.invalid)
    ) {
      this.error = "TIME_TRACKING.INVALID_SESSION_DATE";
      this.cdr.markForCheck();
      return false;
    }
    if (!this.valid()) {
      this.error = this.book
        ? "TIME_TRACKING.INVALID_BOOK_INPUT"
        : "TIME_TRACKING.INVALID_INPUT";
      this.cdr.markForCheck();
      return false;
    }
    return true;
  }

  private valid(): boolean {
    if (this.deleting) return true;
    if (this.editingPages)
      return (
        Number.isSafeInteger(this.pages) &&
        this.pages > 0 &&
        this.pageAfter >= 0 &&
        this.pageAfter <= (this.history?.pageCount ?? 0)
      );
    return (
      Number.isInteger(this.minutes) &&
      this.minutes >= 1 &&
      this.minutes <= 1440
    );
  }
}
